/**
 * Optional client-side image compression, run before upload when the composer's
 * "compress media" toggle is on.
 *
 * Downscales to fit `MAX_DIMENSION` and re-encodes as JPEG. Every failure path
 * returns the original file untouched — a compression problem must never cost
 * the user their attachment.
 */

const MAX_DIMENSION = 1600;
const QUALITY = 0.7;

export interface CompressResult {
  file: File;
  compressed: boolean;
  width?: number;
  height?: number;
}

function readImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (event) => {
      URL.revokeObjectURL(url);
      reject(event);
    };
    img.src = url;
  });
}

export async function compressImage(
  file: File,
  enabled: boolean,
): Promise<CompressResult> {
  const isImage = file.type.startsWith("image/");
  // Re-encoding a GIF drops the animation, so they're never compressed.
  const skip = !isImage || file.type === "image/gif";

  if (!enabled || skip) {
    // Still read natural dimensions for images — the thread uses them to
    // reserve space and avoid layout shift as the image loads.
    if (isImage) {
      try {
        const img = await readImage(file);
        return {
          file,
          compressed: false,
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
      } catch {
        return { file, compressed: false };
      }
    }
    return { file, compressed: false };
  }

  try {
    const img = await readImage(file);
    const { naturalWidth: width, naturalHeight: height } = img;

    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { file, compressed: false, width, height };
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALITY),
    );

    // Keep the re-encode only if it actually saved bytes — a small PNG can grow
    // as a JPEG, and shipping a bigger file would defeat the toggle.
    if (!blob || blob.size >= file.size) {
      return { file, compressed: false, width, height };
    }

    const name = `${file.name.replace(/\.[^.]+$/, "")}.jpg`;
    return {
      file: new File([blob], name, { type: "image/jpeg" }),
      compressed: true,
      width: targetW,
      height: targetH,
    };
  } catch {
    return { file, compressed: false };
  }
}
